import React, { useState, useEffect, useRef } from 'react'
import { useApi } from '../contexts/ApiContext'
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowLeft,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react'
import toast from 'react-hot-toast'

// Collapsible Permissions Component
const CollapsiblePermissions = ({ permissions, maxVisible = 3 }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowTooltip(false)
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip])

  if (!permissions || typeof permissions !== 'object') {
    return <span>No permissions</span>
  }

  const activePermissions = Object.keys(permissions).filter(key => permissions[key] === true)
  
  if (activePermissions.length === 0) {
    return <span>No permissions</span>
  }

  const visiblePermissions = activePermissions.slice(0, maxVisible)
  const remainingCount = activePermissions.length - maxVisible

  const formatPermission = (perm) => {
    return perm.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const showAllPermissions = () => {
    setShowTooltip(true)
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <div className="flex items-center space-x-1">
        <span>{visiblePermissions.map(formatPermission).join(', ')}</span>
        {remainingCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              showAllPermissions()
            }}
            className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
            title={`Click to see all ${activePermissions.length} permissions`}
          >
            +{remainingCount}
          </button>
        )}
      </div>
      
      {showTooltip && (
        <div className="absolute z-50 bg-white text-gray-800 text-xs rounded-lg py-2 px-3 mt-1 left-0 min-w-max max-w-md shadow-lg border border-gray-300 transform -translate-y-full top-0">
          <div className="space-y-1">
            <div className="font-semibold text-green-600 mb-1">All Permissions:</div>
            {activePermissions.map(perm => (
              <div key={perm} className="text-gray-700">
                {formatPermission(perm)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const GenericTable = ({ 
  tableName, 
  title, 
  icon: Icon, 
  iconColor = 'blue',
  searchFields = [],
  hiddenFields = ['id', 'created_at', 'updated_at'],
  primaryKey = 'id',
  defaultFields = [],
  addFields = [],
  editFields = []
}) => {
  const { getTableData, createTableRecord, updateTableRecord, deleteTableRecord } = useApi()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [formData, setFormData] = useState({})
  const [columns, setColumns] = useState([])
  const [allColumns, setAllColumns] = useState([])
  const [selectedColumns, setSelectedColumns] = useState([])
  const [showColumnSelector, setShowColumnSelector] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewRecord, setViewRecord] = useState(null)
  const [columnSearchTerm, setColumnSearchTerm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [sortColumn, setSortColumn] = useState('')
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    loadData()
  }, [])

  // Special effect to handle table column issues and localStorage conflicts
  useEffect(() => {
    if (allColumns.length > 0) {
      // Define valid fields for each table type
      const tableValidFields = {
        'api_logs': ['id', 'idx', 'user_id', 'user_email', 'ip_address', 'user_agent', 'method', 'endpoint', 'query_params', 'request_headers', 'request_body', 'response_status', 'response_headers', 'response_body', 'response_time_ms', 'api_type', 'created_at'],
        'notifications': ['notification_id', 'notification_number', 'customer_id', 'user_email', 'notification_type', 'notification_title', 'notification_status', 'is_read', 'created_at'],
        'customers': ['customer_id', 'customer_number', 'first_name', 'last_name', 'email', 'mobile_phone', 'customer_status', 'city', 'state', 'date_of_birth'],
        'claims': ['claim_id', 'claim_number', 'claim_type', 'claim_stage', 'total_bill_amount', 'approval_status', 'claim_date', 'customer_name', 'hospital_name']
      }
      
      const validFields = tableValidFields[tableName]
      if (validFields) {
        const currentColumns = columns.length > 0 ? columns : selectedColumns
        
        // Check if we have invalid columns (columns that don't belong to this table)
        const hasInvalidColumns = currentColumns.some(col => !validFields.includes(col.key))
        
        if (hasInvalidColumns) {
          console.log(`Detected invalid columns for ${tableName}, fixing...`)
          // Clear localStorage and use correct defaults
          localStorage.removeItem(`${tableName}_selected_columns`)
          
          const correctColumns = allColumns.filter(col => validFields.includes(col.key))
          setSelectedColumns(correctColumns)
          setColumns(correctColumns)
          localStorage.setItem(`${tableName}_selected_columns`, JSON.stringify(correctColumns))
        }
      }
    }
  }, [tableName, allColumns, columns, selectedColumns])

  // Load saved column preferences on component mount
  useEffect(() => {
    const savedColumns = localStorage.getItem(`${tableName}_selected_columns`)
    if (savedColumns) {
      try {
        const parsedColumns = JSON.parse(savedColumns)
        // Validate that saved columns match the current table's default fields
        // If we're on api_logs table, ensure we don't have notification columns
        if (tableName === 'api_logs') {
          const validApiLogFields = ['id', 'idx', 'user_id', 'user_email', 'ip_address', 'user_agent', 'method', 'endpoint', 'query_params', 'request_headers', 'request_body', 'response_status', 'response_headers', 'response_body', 'response_time_ms', 'api_type', 'created_at']
          const hasValidColumns = parsedColumns.every(col => validApiLogFields.includes(col.key))
          
          if (!hasValidColumns) {
            console.log('Invalid saved columns for api_logs, clearing and using defaults')
            localStorage.removeItem(`${tableName}_selected_columns`)
            // Use default fields instead
            if (defaultFields.length > 0 && allColumns.length > 0) {
              const defaultColumns = allColumns.filter(col => defaultFields.includes(col.key))
              setSelectedColumns(defaultColumns)
              setColumns(defaultColumns)
            }
            return
          }
        }
        
        setSelectedColumns(parsedColumns)
        setColumns(parsedColumns)
      } catch (error) {
        console.error('Error loading saved columns:', error)
        // If parsing fails, use default fields
        if (defaultFields.length > 0 && allColumns.length > 0) {
          const defaultColumns = allColumns.filter(col => defaultFields.includes(col.key))
          setSelectedColumns(defaultColumns)
          setColumns(defaultColumns)
        }
      }
    } else {
      // No saved preferences, use default fields if available
      if (defaultFields.length > 0 && allColumns.length > 0) {
        const defaultColumns = allColumns.filter(col => defaultFields.includes(col.key))
        setSelectedColumns(defaultColumns)
        setColumns(defaultColumns)
      }
    }
  }, [tableName, allColumns, defaultFields])

  const loadData = async () => {
    try {
      setLoading(true)
      console.log(`🔍 Loading data for ${tableName}...`)
      const records = await getTableData(tableName)
      console.log(`📊 ${tableName} records loaded:`, records.length)
      setData(records)
      
      // Extract columns from the first record or use default fields
      let allRecordColumns = []
      
      if (records.length > 0) {
        const firstRecord = records[0]
        console.log(`📋 First record keys for ${tableName}:`, Object.keys(firstRecord))
        allRecordColumns = Object.keys(firstRecord)
          .filter(key => !hiddenFields.includes(key))
          .map(key => ({
            key,
            label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            type: getFieldType(firstRecord[key], key)
          }))
      } else {
        // If no records, create columns from defaultFields
        console.log(`📋 No records found for ${tableName}, using default fields:`, defaultFields)
        allRecordColumns = defaultFields
          .filter(key => !hiddenFields.includes(key))
          .map(key => ({
            key,
            label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            type: 'text' // Default type when no data
          }))
      }
      
      console.log(`📋 All columns for ${tableName}:`, allRecordColumns.map(col => col.key))
      setAllColumns(allRecordColumns)
      
      // Only set default columns if no saved preferences exist
      const savedColumns = localStorage.getItem(`${tableName}_selected_columns`)
      if (!savedColumns) {
        // Set default columns or use all columns if no defaults specified
        const initialColumns = defaultFields.length > 0 
          ? allRecordColumns.filter(col => defaultFields.includes(col.key))
          : allRecordColumns
        
        console.log(`${tableName} - Setting default columns:`, initialColumns.map(col => col.key))
        setSelectedColumns(initialColumns)
        setColumns(initialColumns)
      } else {
        console.log(`${tableName} - Using saved columns:`, JSON.parse(savedColumns).map(col => col.key))
      }
      
      // Special handling for api_logs table to ensure correct columns
      if (tableName === 'api_logs') {
        const validApiLogFields = ['id', 'idx', 'user_id', 'user_email', 'ip_address', 'user_agent', 'method', 'endpoint', 'query_params', 'request_headers', 'request_body', 'response_status', 'response_headers', 'response_body', 'response_time_ms', 'api_type', 'created_at']
        const currentColumns = columns.length > 0 ? columns : selectedColumns
        
        // Check if current columns are valid for api_logs
        const hasInvalidColumns = currentColumns.some(col => !validApiLogFields.includes(col.key))
        
        if (hasInvalidColumns || currentColumns.length === 0) {
          console.log('Fixing api_logs columns - using correct default fields')
          const correctColumns = allRecordColumns.filter(col => validApiLogFields.includes(col.key))
          setSelectedColumns(correctColumns)
          setColumns(correctColumns)
          // Save the correct columns to localStorage
          localStorage.setItem(`${tableName}_selected_columns`, JSON.stringify(correctColumns))
        }
      }
    } catch (error) {
      console.error(`Error loading ${tableName}:`, error)
      toast.error(`Failed to load ${title}`)
    } finally {
      setLoading(false)
    }
  }

  const getFieldType = (value, fieldName) => {
    if (fieldName === 'permissions') {
      return 'multiselect' // Force permissions to be multiselect
    }
    
    // Check if field has predefined enum options
    const enumOptions = getFieldOptions(fieldName)
    if (enumOptions.length > 0) {
      return 'select'
    }
    
    if (value === null || value === undefined) return 'text'
    if (typeof value === 'boolean') return 'checkbox'
    if (typeof value === 'number') return 'number'
    if (Array.isArray(value)) return 'multiselect'
    if (typeof value === 'string') {
      if (value.includes('@')) return 'email'
      if (value.match(/^\d{4}-\d{2}-\d{2}/)) return 'date'
      if (value.length > 100) return 'textarea'
      
      // Check for comma-separated values (like "Cardiology,Orthopedics,Neurology")
      if (value.includes(',') && !value.includes('@') && value.length < 1000) {
        return 'multiselect'
      }
      
      // Check for JSON objects (starts with { or [)
      if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
        return 'json'
      }
    }
    
    // Check for password field
    if (fieldName === 'password' || fieldName === 'password_hash') return 'password'
    
    // Check for date fields by name pattern
    if (fieldName.includes('_date') || fieldName.includes('_at') || fieldName === 'date' || fieldName === 'created_at' || fieldName === 'updated_at') {
      return 'date'
    }
    
    // Check for specific field names that should be dropdowns
    const dropdownFields = {
      'marital_status': ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED', 'SEPARATED'],
      'gender': ['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY'],
      'customer_status': ['ACTIVE', 'INACTIVE', 'PENDING', 'SUSPENDED'],
      'risk_band': ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'],
      'segment': ['INDIVIDUAL', 'SME', 'CORPORATE', 'ENTERPRISE'],
      'preferred_channel': ['PHONE', 'EMAIL', 'SMS', 'WHATSAPP', 'ONLINE'],
      'preferred_language': ['EN', 'HI', 'TA', 'TE', 'BN'],
      'annual_income_band': ['0-5L', '5L-10L', '10L-25L', '25L+'],
      'source_of_funds': ['SALARY', 'BUSINESS', 'INVESTMENT', 'INHERITANCE', 'OTHER'],
      'country': ['IN', 'US', 'UK', 'AU', 'CA'],
      'payment_method': ['CREDIT_CARD', 'DEBIT_CARD', 'BANK_TRANSFER', 'CASH', 'CHECK'],
      'policy_status': ['ACTIVE', 'INACTIVE', 'EXPIRED', 'CANCELLED', 'PENDING'],
      'claim_status': ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PAID'],
      'document_type': ['ID_PROOF', 'ADDRESS_PROOF', 'MEDICAL_CERTIFICATE', 'POLICY_DOCUMENT', 'CLAIM_FORM'],
      'disease_category': ['CARDIOVASCULAR', 'RESPIRATORY', 'NEUROLOGICAL', 'CANCER', 'DIABETES', 'OTHER'],
      'role': ['ADMIN', 'MANAGER', 'AGENT', 'CUSTOMER_SERVICE', 'MEDICAL_REVIEWER', 'FINANCE', 'SUPER_ADMIN'],
      'department': ['CLAIMS', 'UNDERWRITING', 'CUSTOMER_SERVICE', 'FINANCE', 'MEDICAL', 'IT'],
      // CLAIMS TABLE ENUMS - Add these specific fields
      'claim_type': ['INPATIENT', 'OUTPATIENT', 'DAYCARE', 'EMERGENCY', 'PRE_APPROVAL', 'MATERNITY', 'DEATH', 'MATURITY', 'SURRENDER', 'ACCIDENTAL_DEATH', 'CRITICAL_ILLNESS', 'DISABILITY'],
      'claim_category': ['MEDICAL', 'SURGICAL', 'DIAGNOSTIC', 'PREVENTIVE', 'MATERNITY', 'DENTAL', 'VISION', 'MENTAL_HEALTH', 'LIFE_INSURANCE'],
      'claim_stage': ['PRE_TREATMENT', 'POST_TREATMENT', 'SUBMITTED', 'UNDER_REVIEW', 'REQUIRES_DOCUMENTS', 'UNDER_INVESTIGATION', 'APPROVED', 'REJECTED', 'PAID', 'CANCELLED', 'ON_HOLD'],
      'claim_mode': ['CASHLESS', 'REIMBURSEMENT', 'PARTIAL_CASHLESS', 'DIRECT_SETTLEMENT'],
      'approval_status': ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PARTIALLY_APPROVED'],
      'payment_status': ['PENDING', 'PROCESSING', 'PAID', 'FAILED', 'CANCELLED']
    }
    
    if (dropdownFields[fieldName]) {
      return 'select'
    }
    
    return 'text'
  }

  // Function to check if current logged-in user has permission for an action
  const hasPermission = (action) => {
    try {
      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
      
      console.log('🔍 hasPermission - Current user:', currentUser)
      console.log('🔍 hasPermission - Action:', action)
      
      // Check if user is a customer - customers don't need permissions
      const currentPath = window.location.pathname
      const isCustomerDashboard = (currentPath.includes('/customer') && !currentPath.includes('/admin')) || currentPath.includes('/public')
      
      if (isCustomerDashboard) {
        console.log('🔍 hasPermission - Customer dashboard, no permissions')
        return false // Customers don't have permissions
      }
      
      // OVERRIDE: Always show actions in admin dashboard
      if (currentPath.includes('/admin')) {
        console.log('🔍 hasPermission - Admin dashboard override, granting permission')
        return true
      }
      
      // Check if user has admin role (check both direct role and user_metadata.role)
      const userRole = currentUser.role || currentUser.user_metadata?.role
      console.log('🔍 hasPermission - User role:', userRole)
      
      if (userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'super_admin' || userRole === 'admin' || userRole === 'SUPER_ADMIN' || userRole === 'SUPER_ADMIN') {
        console.log('🔍 hasPermission - Admin role detected, granting permission')
        return true
      }
      
      // Additional check for admin roles in user_metadata
      if (currentUser.user_metadata?.role === 'ADMIN' || currentUser.user_metadata?.role === 'SUPER_ADMIN' || currentUser.user_metadata?.role === 'super_admin' || currentUser.user_metadata?.role === 'admin') {
        console.log('🔍 hasPermission - Admin role in metadata detected, granting permission')
        return true
      }
      
      // Fallback: Check if user is in admin dashboard and has admin email
      if (currentPath.includes('/admin') && (currentUser.email === 'admin@yourinsurance.com' || currentUser.email?.includes('admin'))) {
        console.log('🔍 hasPermission - Admin dashboard with admin email, granting permission')
        return true
      }
      
      // Additional fallback: Always grant permissions in admin dashboard
      if (currentPath.includes('/admin')) {
        console.log('🔍 hasPermission - Admin dashboard detected, granting permission')
        return true
      }
      
      // Check for permissions in user_metadata
      const permissions = currentUser.permissions || currentUser.user_metadata?.permissions
      console.log('🔍 hasPermission - Permissions:', permissions)
      
      if (!permissions || typeof permissions !== 'object') {
        console.log('🔍 hasPermission - No permissions object found')
        // Fallback: if we're in admin dashboard, always grant permissions
        if (currentPath.includes('/admin')) {
          console.log('🔍 hasPermission - Admin dashboard detected, granting permission')
          return true
        }
        return false
      }
      
      // Check for full_access first
      if (permissions.full_access === true) {
        console.log('🔍 hasPermission - Full access granted')
        return true
      }
      
      // Define permission mappings for different actions
      const permissionMap = {
        'view': ['can_view_claims', 'can_view_all', 'full_access'],
        'edit': ['can_edit_claims', 'can_manage_all', 'full_access'],
        'delete': ['can_delete_users', 'can_manage_all', 'full_access']
      }
      
      const requiredPermissions = permissionMap[action] || []
      const hasRequiredPermission = requiredPermissions.some(perm => permissions[perm] === true)
      
      console.log('🔍 hasPermission - Required permissions:', requiredPermissions)
      console.log('🔍 hasPermission - Has required permission:', hasRequiredPermission)
      
      return hasRequiredPermission
    } catch (error) {
      console.error('Error checking permissions:', error)
      // Default to showing all actions if there's an error
      return true
    }
  }

  const getFieldOptions = (fieldName) => {
    // Hide permissions field for customer users
    const currentPath = window.location.pathname
    const isCustomerDashboard = currentPath.includes('/customer') || currentPath.includes('/public')
    
    if (fieldName === 'permissions' && isCustomerDashboard) {
      return [] // Hide permissions for customers
    }
    
    const dropdownFields = {
      // CUSTOMERS TABLE ENUMS
      'marital_status': ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED', 'SEPARATED'],
      'gender': ['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY'],
      'customer_status': ['ACTIVE', 'INACTIVE', 'BLACKLISTED', 'DECEASED'],
      'kyc_status': ['PENDING', 'VERIFIED', 'FAILED'],
      'risk_band': ['LOW', 'MED', 'HIGH'],
      'segment': ['RETAIL', 'SME', 'HNI'],
      'preferred_channel': ['EMAIL', 'SMS', 'PHONE', 'WHATSAPP'],
      'preferred_language': ['EN', 'HI', 'TA', 'TE', 'BN'],
      'annual_income_band': ['0-5L', '5-10L', '10-25L', '25L+'],
      'source_of_funds': ['SALARY', 'BUSINESS', 'INVESTMENT', 'OTHER'],
      'national_id_type': ['AADHAAR', 'PAN', 'PASSPORT', 'DRIVING_LICENSE'],
      'country': ['IN', 'US', 'UK', 'AU', 'CA'],
      
      // POLICIES TABLE ENUMS
      'policy_status': ['ACTIVE', 'INACTIVE', 'EXPIRED', 'CANCELLED', 'SURRENDERED'],
      'underwriting_status': ['PENDING', 'APPROVED', 'REJECTED', 'ON_HOLD'],
      'renewal_status': ['ELIGIBLE', 'NOT_ELIGIBLE', 'PENDING_RENEWAL'],
      'premium_frequency': ['YEARLY', 'HALF_YEARLY', 'QUARTERLY', 'MONTHLY'],
      'policy_holder_relationship': ['SELF', 'SPOUSE', 'CHILD', 'PARENT'],
      
      // CLAIMS TABLE ENUMS
      'claim_type': ['INPATIENT', 'OUTPATIENT', 'DAYCARE', 'EMERGENCY', 'PRE_APPROVAL', 'MATERNITY', 'DEATH', 'MATURITY', 'SURRENDER', 'ACCIDENTAL_DEATH', 'CRITICAL_ILLNESS', 'DISABILITY'],
      'claim_category': ['MEDICAL', 'SURGICAL', 'DIAGNOSTIC', 'PREVENTIVE', 'MATERNITY', 'DENTAL', 'VISION', 'MENTAL_HEALTH', 'LIFE_INSURANCE'],
      'claim_stage': ['PRE_TREATMENT', 'POST_TREATMENT', 'SUBMITTED', 'UNDER_REVIEW', 'REQUIRES_DOCUMENTS', 'UNDER_INVESTIGATION', 'APPROVED', 'REJECTED', 'PAID', 'CANCELLED', 'ON_HOLD'],
      'claim_mode': ['CASHLESS', 'REIMBURSEMENT', 'PARTIAL_CASHLESS', 'DIRECT_SETTLEMENT'],
      'claim_status': ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PAID', 'CLOSED'],
      'processing_status': ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'],
      'processing_priority': ['LOW', 'NORMAL', 'MEDIUM', 'HIGH', 'URGENT', 'CRITICAL'],
      'approval_status': ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PARTIALLY_APPROVED'],
      'payment_status': ['PENDING', 'PROCESSING', 'PAID', 'FAILED', 'CANCELLED'],
      'deficiency_status': ['NONE', 'PENDING', 'RESOLVED'],
      'medical_review_status': ['NOT_REQUIRED', 'PENDING', 'COMPLETED'],
      'investigation_status': ['NOT_REQUIRED', 'PENDING', 'COMPLETED'],
      
      // HOSPITALS TABLE ENUMS
      'network_status': ['IN_NETWORK', 'OUT_OF_NETWORK', 'TIER_1', 'TIER_2'],
      'hospital_type': ['GOVERNMENT', 'PRIVATE', 'SEMI_PRIVATE', 'MULTI_SPECIALTY', 'SINGLE_SPECIALTY'],
      
      // DISEASES TABLE ENUMS
      'severity_level': ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'],
      'disease_category': ['CARDIOVASCULAR', 'RESPIRATORY', 'NEUROLOGICAL', 'CANCER', 'DIABETES', 'OTHER'],
      'verification_priority': ['LOW', 'NORMAL', 'HIGH', 'URGENT'],
      'regulatory_status': ['APPROVED', 'RESTRICTED', 'EXCLUDED'],
      'disease_status': ['ACTIVE', 'INACTIVE', 'DEPRECATED', 'UNDER_REVIEW'],
      'status': ['ACTIVE', 'INACTIVE', 'DEPRECATED', 'UNDER_REVIEW'],
      
      // POLICY_TYPES TABLE ENUMS
      'product_category': ['HEALTH', 'LIFE', 'MOTOR', 'PROPERTY', 'TRAVEL'],
      'product_subcategory': ['INDIVIDUAL', 'FAMILY_FLOATER', 'GROUP', 'SENIOR_CITIZEN'],
      'product_variant': ['BASIC', 'ENHANCED', 'PREMIUM', 'ULTRA'],
      
      // PAYMENTS TABLE ENUMS
      'payment_type': ['CLAIM_SETTLEMENT', 'REFUND', 'ADJUSTMENT', 'PREMIUM_PAYMENT', 'CASHBACK'],
      'payment_method': ['BANK_TRANSFER', 'CHEQUE', 'CASH', 'ONLINE', 'CARD', 'UPI', 'NEFT', 'RTGS'],
      
      // DOCUMENTS TABLE ENUMS
      'document_type': ['ID_PROOF', 'ADDRESS_PROOF', 'MEDICAL_CERTIFICATE', 'POLICY_DOCUMENT', 'CLAIM_FORM', 'DISCHARGE_SUMMARY', 'BILLS', 'RECEIPTS'],
      'verification_status': ['PENDING', 'UNDER_REVIEW', 'VERIFIED', 'REJECTED', 'EXPIRED'],
      
      // CLAIM_DECISIONS TABLE ENUMS
      'decision_status': ['PENDING', 'APPROVED', 'REJECTED', 'UNDER_REVIEW'],
      'decision_type': ['MEDICAL', 'FINANCIAL', 'DOCUMENT', 'AUTO', 'MANUAL', 'ESCALATED'],
      'decision_result': ['PENDING', 'APPROVED', 'REJECTED'],
      
      // SESSIONS TABLE ENUMS
      'user_type': ['CUSTOMER', 'EMPLOYEE', 'ADMIN'],
      'event_type': ['LOGIN', 'LOGOUT'],
      
      // USERS TABLE ENUMS
      'role': ['ADMIN', 'MANAGER', 'AGENT', 'CUSTOMER_SERVICE', 'MEDICAL_REVIEWER', 'FINANCE', 'SUPER_ADMIN'],
      'department': ['CLAIMS', 'UNDERWRITING', 'CUSTOMER_SERVICE', 'FINANCE', 'MEDICAL', 'IT'],
      
      // MULTI-SELECT FIELDS
      'permissions': [
        'can_view_claims', 'can_help_customers', 'full_access', 'can_manage_all', 
        'can_create_users', 'can_delete_users', 'can_view_all', 'can_edit_claims', 
        'can_manage_customers', 'can_approve_claims', 'can_reject_claims', 
        'can_manage_policies', 'can_view_reports', 'can_manage_payments'
      ],
      
      // MEDICAL SPECIALTIES (for comma-separated values)
      'specialties': [
        'Cardiology', 'Orthopedics', 'Neurology', 'Oncology', 'General Surgery', 
        'Internal Medicine', 'Pediatrics', 'Gynecology', 'Dermatology', 'Ophthalmology',
        'ENT', 'Psychiatry', 'Radiology', 'Anesthesiology', 'Emergency Medicine',
        'Pathology', 'Urology', 'Gastroenterology', 'Endocrinology', 'Pulmonology'
      ],
      
      // HOSPITAL DEPARTMENTS
      'departments': [
        'Emergency', 'ICU', 'Surgery', 'Cardiology', 'Neurology', 'Oncology',
        'Pediatrics', 'Gynecology', 'Orthopedics', 'Radiology', 'Laboratory',
        'Pharmacy', 'Administration', 'Nursing', 'Rehabilitation'
      ]
    }
    
    return dropdownFields[fieldName] || []
  }

  // Function to detect if a field should be a dropdown based on data analysis
  const detectFieldType = (fieldKey, sampleData) => {
    // Check if field has predefined options
    const predefinedOptions = getFieldOptions(fieldKey)
    if (predefinedOptions.length > 0) {
      return 'select'
    }
    
    // Analyze sample data to detect enum-like fields
    if (sampleData && sampleData.length > 0) {
      const values = sampleData.map(record => record[fieldKey]).filter(val => val !== null && val !== undefined && val !== '')
      const uniqueValues = [...new Set(values)]
      
      // If there are 2-20 unique values and they look like enum values, treat as select
      if (uniqueValues.length >= 2 && uniqueValues.length <= 20) {
        // Check if values look like enum (not too long, not numbers, etc.)
        const isEnumLike = uniqueValues.every(val => 
          typeof val === 'string' && 
          val.length <= 50 && 
          !/^\d+$/.test(val) && // Not just numbers
          !/^\d{4}-\d{2}-\d{2}/.test(val) // Not dates
        )
        
        if (isEnumLike) {
          return 'select'
        }
      }
    }
    
    return 'text' // Default to text input
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1)
  }

  const handleColumnToggle = (columnKey) => {
    const isSelected = selectedColumns.some(col => col.key === columnKey)
    if (isSelected) {
      setSelectedColumns(selectedColumns.filter(col => col.key !== columnKey))
    } else {
      const columnToAdd = allColumns.find(col => col.key === columnKey)
      if (columnToAdd) {
        setSelectedColumns([...selectedColumns, columnToAdd])
      }
    }
  }

  const applyColumnSelection = () => {
    setColumns(selectedColumns)
    // Save column preferences to localStorage
    localStorage.setItem(`${tableName}_selected_columns`, JSON.stringify(selectedColumns))
    setShowColumnSelector(false)
  }

  const resetToDefaults = () => {
    // Clear any saved preferences first
    localStorage.removeItem(`${tableName}_selected_columns`)
    
    // Use default fields if available, otherwise use all columns
    const defaultColumns = defaultFields.length > 0 
      ? allColumns.filter(col => defaultFields.includes(col.key))
      : allColumns
    
    setSelectedColumns(defaultColumns)
    setColumns(defaultColumns)
    localStorage.setItem(`${tableName}_selected_columns`, JSON.stringify(defaultColumns))
    console.log(`${tableName} - Reset to defaults:`, defaultColumns.map(col => col.key))
  }

  const openViewModal = (record) => {
    setViewRecord(record)
    setShowViewModal(true)
  }

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  const filteredData = data.filter(record => {
    if (!searchTerm) return true
    return searchFields.length > 0 
      ? searchFields.some(field => 
          record[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      : Object.values(record).some(value => 
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
  })

  // Sort the filtered data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0
    
    const aValue = a[sortColumn]
    const bValue = b[sortColumn]
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? 1 : -1
    if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? -1 : 1
    
    // Handle different data types
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.toLowerCase().localeCompare(bValue.toLowerCase())
        : bValue.toLowerCase().localeCompare(aValue.toLowerCase())
    }
    
    // Handle dates
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }
    
    // Fallback to string comparison
    const aStr = String(aValue).toLowerCase()
    const bStr = String(bValue).toLowerCase()
    return sortDirection === 'asc' 
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr)
  })

  const totalPages = Math.ceil(sortedData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentData = sortedData.slice(startIndex, endIndex)

  const handleAdd = async (e) => {
    e.preventDefault()
    try {
      const addData = { ...formData }
      
      // Filter out empty timestamp fields that would cause database errors
      Object.keys(addData).forEach(key => {
        if (addData[key] === '' || addData[key] === null || addData[key] === undefined) {
          // Check if it's a timestamp field
          if (key.includes('_at') || key.includes('_date') || key.includes('_time') || 
              key === 'last_login' || key === 'login_time' || key === 'logout_time' ||
              key === 'created_at' || key === 'updated_at') {
            delete addData[key]
          }
        }
      })
      
      // Password field is now handled directly as password_hash
      
      // Handle permissions field - ensure it's properly formatted as object
      if (addData.permissions && typeof addData.permissions === 'object' && !Array.isArray(addData.permissions)) {
        // Permissions is already an object, keep as is
      } else if (addData.permissions) {
        // Try to parse if it's a string
        try {
          addData.permissions = JSON.parse(addData.permissions)
        } catch (e) {
          addData.permissions = {}
        }
      }
      
      await createTableRecord(tableName, addData)
      toast.success(`${title} added successfully`)
      setShowAddModal(false)
      setFormData({})
      loadData()
    } catch (error) {
      console.error(`Error adding ${title}:`, error)
      toast.error(`Failed to add ${title}`)
    }
  }

  const handleEdit = async (e) => {
    e.preventDefault()
    try {
      // Filter out system-generated fields that should never be updated
      const systemFields = [
        primaryKey, // Primary key (customer_id, claim_id, etc.)
        'created_at', 'updated_at', 'created_by', 'updated_by',
        'record_version', 'version', 'id', 'uuid',
        // Auto-generated numbers - never editable from UI
        'customer_number', 'claim_number', 'policy_number', 'payment_number',
        'document_number', 'session_number', 'user_number', 'hospital_number',
        'disease_number', 'coverage_rule_number', 'decision_number',
        // Security fields (keep password_hash as it's a legitimate field to update)
        'national_id_hash', 'token_hash',
        // System tracking fields
        'last_login_at', 'login_id', 'external_ref', 'crm_account_id',
        'kyc_verified_at', 'marketing_consent_at', 'privacy_consent_at'
      ]
      
      const updateData = { ...formData }
      systemFields.forEach(field => {
        delete updateData[field]
      })
      
      // Filter out empty timestamp fields that would cause database errors
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '' || updateData[key] === null || updateData[key] === undefined) {
          // Check if it's a timestamp field
          if (key.includes('_at') || key.includes('_date') || key.includes('_time') || 
              key === 'last_login' || key === 'login_time' || key === 'logout_time' ||
              key === 'created_at' || key === 'updated_at') {
            delete updateData[key]
          }
        }
      })
      
      // Password field is now handled directly as password_hash
      
      // Handle permissions field - ensure it's properly formatted as object
      if (updateData.permissions && typeof updateData.permissions === 'object' && !Array.isArray(updateData.permissions)) {
        // Permissions is already an object, keep as is
      } else if (updateData.permissions) {
        // Try to parse if it's a string
        try {
          updateData.permissions = JSON.parse(updateData.permissions)
        } catch (e) {
          updateData.permissions = {}
        }
      }
      
      await updateTableRecord(tableName, selectedRecord[primaryKey], updateData, primaryKey)
      toast.success(`${title} updated successfully`)
      setShowEditModal(false)
      setSelectedRecord(null)
      loadData()
    } catch (error) {
      console.error(`Error updating ${title}:`, error)
      toast.error(`Failed to update ${title}`)
    }
  }

  const handleDelete = async (record) => {
    if (window.confirm(`Are you sure you want to delete this ${title.toLowerCase()}?`)) {
      try {
        await deleteTableRecord(tableName, record[primaryKey], primaryKey)
        toast.success(`${title} deleted successfully`)
        loadData()
      } catch (error) {
        console.error(`Error deleting ${title}:`, error)
        toast.error(`Failed to delete ${title}`)
      }
    }
  }

  const openEditModal = (record) => {
    setSelectedRecord(record)
    setShowPassword(false)
    
    // Filter out system fields that shouldn't be editable
    const systemFields = [
      primaryKey, // Primary key (customer_id, claim_id, etc.)
      'created_at', 'updated_at', 'created_by', 'updated_by',
      'record_version', 'version', 'id', 'uuid',
      // Auto-generated numbers - never editable from UI
      'customer_number', 'claim_number', 'policy_number', 'payment_number',
      'document_number', 'session_number', 'user_number', 'hospital_number',
      'disease_number', 'coverage_rule_number', 'decision_number',
      // Security fields (keep password_hash as it's a legitimate field to update)
      'national_id_hash', 'token_hash',
      // System tracking fields
      'last_login_at', 'login_id', 'external_ref', 'crm_account_id',
      'kyc_verified_at', 'marketing_consent_at', 'privacy_consent_at'
    ]
    
    // Format dates for editing and exclude system fields
    const formattedRecord = { ...record }
    Object.keys(formattedRecord).forEach(key => {
      if (systemFields.includes(key)) {
        delete formattedRecord[key] // Remove system fields
      } else if (formattedRecord[key] && typeof formattedRecord[key] === 'string' && formattedRecord[key].includes('T')) {
        // Convert ISO date to YYYY-MM-DD format for date inputs
        const date = new Date(formattedRecord[key])
        if (!isNaN(date.getTime())) {
          formattedRecord[key] = date.toISOString().split('T')[0]
        }
      } else if (key.includes('_date') || key.includes('_at') || key === 'date') {
        // Handle date fields - convert to YYYY-MM-DD format
        if (formattedRecord[key] && typeof formattedRecord[key] === 'string') {
          const date = new Date(formattedRecord[key])
          if (!isNaN(date.getTime())) {
            formattedRecord[key] = date.toISOString().split('T')[0]
          }
        }
      } else if (key === 'permissions') {
        // Handle permissions field - convert to proper object format
        let permissions = {}
        if (typeof formattedRecord[key] === 'string') {
          try {
            permissions = JSON.parse(formattedRecord[key])
          } catch (e) {
            permissions = {}
          }
        } else if (typeof formattedRecord[key] === 'object' && formattedRecord[key] !== null) {
          permissions = formattedRecord[key]
        }
        
        // Create a complete permissions object with all options
        const allPermissions = {
          'can_view_claims': false,
          'can_help_customers': false,
          'full_access': false,
          'can_manage_all': false,
          'can_create_users': false,
          'can_delete_users': false,
          'can_view_all': false,
          'can_edit_claims': false,
          'can_manage_customers': false,
          'can_approve_claims': false,
          'can_reject_claims': false,
          'can_manage_policies': false,
          'can_view_reports': false,
          'can_manage_payments': false
        }
        
        // Merge existing permissions
        Object.keys(permissions).forEach(key => {
          if (allPermissions.hasOwnProperty(key)) {
            allPermissions[key] = permissions[key] === true
          }
        })
        
        formattedRecord[key] = allPermissions
      } else {
        // Ensure all other fields have proper values (not null/undefined)
        if (formattedRecord[key] === null || formattedRecord[key] === undefined) {
          formattedRecord[key] = ''
        }
      }
    })
    
    // Password field is now handled directly as password_hash
    
    console.log('Setting form data for edit:', formattedRecord)
    setFormData(formattedRecord)
    setShowEditModal(true)
  }

  const renderFormField = (column) => {
    const { key, type } = column
    const value = formData[key] || ''
    
    switch (type) {
      case 'textarea':
        return (
          <textarea
            key={key}
            value={value}
            onChange={(e) => setFormData({...formData, [key]: e.target.value})}
            className="input-field"
            style={{fontSize: '10px'}}
            rows={3}
            placeholder={`Enter ${column.label}`}
          />
        )
      case 'checkbox':
        return (
          <input
            key={key}
            type="checkbox"
            checked={value}
            onChange={(e) => setFormData({...formData, [key]: e.target.checked})}
            className="rounded"
          />
        )
      case 'date':
        // Handle date fields - always show datetime picker but send in Supabase format
        const dateValue = value ? (typeof value === 'string' && value.includes('T') 
          ? new Date(value).toISOString().split('T')[0] 
          : value) : ''
        return (
          <div key={key} className="relative">
            <input
              type="datetime-local"
              value={dateValue}
              onChange={(e) => {
                // Convert to Supabase datetime format: 2024-01-15 00:00:00+00
                const date = new Date(e.target.value)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                const hours = String(date.getHours()).padStart(2, '0')
                const minutes = String(date.getMinutes()).padStart(2, '0')
                const seconds = String(date.getSeconds()).padStart(2, '0')
                const supabaseFormat = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}+00`
                setFormData({...formData, [key]: supabaseFormat})
              }}
              className="input-field pr-8"
              style={{fontSize: '10px'}}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        )
      case 'json':
        // Handle JSON objects - show as formatted textarea
        let jsonValue = value
        if (typeof value === 'string') {
          try {
            jsonValue = JSON.stringify(JSON.parse(value), null, 2)
          } catch (e) {
            jsonValue = value
          }
        } else if (typeof value === 'object') {
          jsonValue = JSON.stringify(value, null, 2)
        }
        return (
          <textarea
            key={key}
            value={jsonValue}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value)
                setFormData({...formData, [key]: parsed})
              } catch (e) {
                // Keep as string if not valid JSON
                setFormData({...formData, [key]: e.target.value})
              }
            }}
            className="input-field font-mono"
            style={{fontSize: '10px'}}
            rows={4}
            placeholder="Enter JSON object"
          />
        )
      case 'email':
        return (
          <input
            key={key}
            type="email"
            value={value}
            onChange={(e) => setFormData({...formData, [key]: e.target.value})}
            className="input-field"
            style={{fontSize: '10px'}}
            placeholder={`Enter ${column.label}`}
          />
        )
      case 'password':
        return (
          <div key={key} className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={value}
              onChange={(e) => setFormData({...formData, [key]: e.target.value})}
              className="input-field pr-10"
              style={{fontSize: '10px'}}
              placeholder={`Enter ${column.label}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        )
      case 'number':
        return (
          <div key={key} className="relative">
            <input
              type="number"
              value={value}
              onChange={(e) => setFormData({...formData, [key]: parseFloat(e.target.value) || 0})}
              className="input-field pr-16"
              style={{fontSize: '10px'}}
              placeholder={`Enter ${column.label}`}
            />
            <div className="absolute inset-y-0 right-0 flex flex-col">
              <button
                type="button"
                onClick={() => setFormData({...formData, [key]: (parseFloat(value) || 0) + 1})}
                className="flex-1 px-2 text-gray-400 hover:text-gray-600 border-l border-gray-300 hover:bg-gray-50"
                style={{fontSize: '9px'}}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, [key]: Math.max(0, (parseFloat(value) || 0) - 1)})}
                className="flex-1 px-2 text-gray-400 hover:text-gray-600 border-l border-gray-300 hover:bg-gray-50"
                style={{fontSize: '9px'}}
              >
                ▼
              </button>
            </div>
          </div>
        )
      case 'decimal':
        return (
          <div key={key} className="relative">
            <input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => setFormData({...formData, [key]: parseFloat(e.target.value) || 0})}
              className="input-field pr-16"
              style={{fontSize: '10px'}}
              placeholder={`Enter ${column.label}`}
            />
            <div className="absolute inset-y-0 right-0 flex flex-col">
              <button
                type="button"
                onClick={() => setFormData({...formData, [key]: ((parseFloat(value) || 0) + 0.01).toFixed(2)})}
                className="flex-1 px-2 text-gray-400 hover:text-gray-600 border-l border-gray-300 hover:bg-gray-50"
                style={{fontSize: '9px'}}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, [key]: Math.max(0, ((parseFloat(value) || 0) - 0.01).toFixed(2))})}
                className="flex-1 px-2 text-gray-400 hover:text-gray-600 border-l border-gray-300 hover:bg-gray-50"
                style={{fontSize: '9px'}}
              >
                ▼
              </button>
            </div>
          </div>
        )
      case 'select':
        const options = getFieldOptions(key)
        const selectValue = value || ''
        console.log(`Select field ${key}:`, { 
          value: selectValue, 
          options, 
          isValueInOptions: options.includes(selectValue),
          rawValue: value,
          fieldKey: key
        })
        
        // If the value doesn't match any option, add it to the options
        const allOptions = options.includes(selectValue) ? options : [selectValue, ...options].filter(Boolean)
        
        return (
          <select
            key={key}
            value={selectValue}
            onChange={(e) => setFormData({...formData, [key]: e.target.value})}
            className="input-field"
            style={{fontSize: '10px', backgroundColor: 'white'}}
          >
            <option value="">Select {column.label}</option>
            {allOptions.map(option => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        )
      case 'multiselect':
        const multiOptions = getFieldOptions(key)
        
        // Handle permissions specifically
        if (key === 'permissions') {
          const permissions = typeof value === 'object' && value !== null ? value : {}
          
          return (
            <div key={key} className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block font-medium text-gray-700" style={{fontSize: '10px'}}>{column.label}</label>
                <button
                  type="button"
                  onClick={() => {
                    const allChecked = multiOptions.every(option => permissions[option] === true)
                    const newPermissions = {}
                    multiOptions.forEach(option => {
                      newPermissions[option] = !allChecked
                    })
                    setFormData({
                      ...formData,
                      [key]: newPermissions
                    })
                  }}
                  className="text-blue-600 hover:text-blue-800 underline"
                  style={{fontSize: '10px'}}
                >
                  {multiOptions.every(option => permissions[option] === true) ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                {multiOptions.map(option => {
                  const isChecked = permissions[option] === true
                  
                  return (
                    <label key={option} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          setFormData({
                            ...formData, 
                            [key]: {
                              ...permissions,
                              [option]: e.target.checked
                            }
                          })
                        }}
                        className="rounded"
                      />
                      <span style={{fontSize: '10px'}}>{option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        }
        
        // Handle other multiselect fields (arrays and comma-separated strings)
        let currentValues = []
        if (Array.isArray(value)) {
          currentValues = value
        } else if (typeof value === 'string' && value.includes(',')) {
          // Handle comma-separated values like "Cardiology,Orthopedics,Neurology"
          currentValues = value.split(',').map(item => item.trim()).filter(Boolean)
        } else if (typeof value === 'string' && value) {
          currentValues = [value]
        }
        
        return (
          <div key={key} className="space-y-2">
            <label className="block font-medium text-gray-700" style={{fontSize: '10px'}}>{column.label}</label>
            <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
              {multiOptions.map(option => {
                const isChecked = currentValues.includes(option)
                
                return (
                  <label key={option} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...currentValues, option]
                          : currentValues.filter(v => v !== option)
                        setFormData({...formData, [key]: newValues})
                      }}
                      className="rounded"
                    />
                    <span style={{fontSize: '10px'}}>{option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </label>
                )
              })}
            </div>
            {/* Show current values as comma-separated string */}
            {currentValues.length > 0 && (
              <div className="text-gray-500 mt-1" style={{fontSize: '10px'}}>
                Current: {currentValues.join(', ')}
              </div>
            )}
          </div>
        )
      default:
        return (
          <input
            key={key}
            type="text"
            value={value}
            onChange={(e) => setFormData({...formData, [key]: e.target.value})}
            className="input-field"
            style={{fontSize: '10px'}}
            placeholder={`Enter ${column.label}`}
          />
        )
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden" style={{maxWidth: '100%', width: '100%'}}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Icon className={`h-8 w-8 text-${iconColor}-600`} />
          <div>
            <h1 className="font-bold text-gray-900" style={{fontSize: '14px'}}>{title} Management</h1>
            <p className="text-gray-600" style={{fontSize: '12px'}}>Manage {title.toLowerCase()} records</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadData}
            className="btn-secondary flex items-center space-x-2 ml-4"
            style={{fontSize: '10px'}}
            title="Refresh data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              setShowAddModal(true)
              setShowPassword(false)
            }}
            className="btn-primary flex items-center space-x-2 ml-4"
            style={{fontSize: '10px'}}
          >
            <Plus className="h-4 w-4" />
            <span>Add {title}</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              style={{fontSize: '10px'}}
            />
          </div>
          <button
            onClick={() => {
              setColumnSearchTerm('') // Clear search when opening
              setShowColumnSelector(true)
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors ml-4"
          >
            <Filter className="h-4 w-4" />
            <span style={{fontSize: '10px'}}>Columns</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden" style={{maxWidth: '100%', width: '100%'}}>
        <div className="overflow-x-auto overflow-y-hidden" style={{maxWidth: '100%', maxHeight: '70vh', width: '100%', position: 'relative'}}>
          <table className="divide-y divide-gray-200 table-auto w-full">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-0.5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${
                      column.key === 'customer_number' ? 'w-32' :
                      column.key === 'first_name' ? 'w-24' :
                      column.key === 'last_name' ? 'w-24' :
                      column.key === 'email' ? 'w-48' :
                      column.key === 'mobile_phone' ? 'w-32' :
                      column.key === 'city' ? 'w-24' :
                      column.key === 'state' ? 'w-24' :
                      column.key === 'customer_status' ? 'w-32' :
                      column.key === 'date_of_birth' ? 'w-32' :
                      column.key === 'hospital_code' ? 'w-32' :
                      column.key === 'hospital_name' ? 'w-64' :
                      column.key === 'hospital_city' ? 'w-24' :
                      column.key === 'hospital_state' ? 'w-24' :
                      column.key === 'network_status' ? 'w-32' :
                      column.key === 'hospital_type' ? 'w-40' :
                      column.key === 'disease_name' ? 'w-80' :
                      column.key === 'icd_code' ? 'w-24' :
                      column.key === 'category' ? 'w-32' :
                      column.key === 'severity_level' ? 'w-32' :
                      column.key === 'coverage_percentage' ? 'w-32' :
                      column.key === 'status' ? 'w-24' :
                      'w-auto'
                    }`}
                    style={{fontSize: '9px'}}
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.label}</span>
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-0.5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider w-24 sticky right-0 bg-gray-50 z-10" style={{fontSize: '9px'}}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.map((record, index) => (
                <tr key={record[primaryKey] || index} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td key={column.key} className="px-0.5 py-4">
                      <div 
                        className="text-gray-900 break-words" 
                        style={{
                          fontSize: '9px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          maxWidth: column.key === 'hospital_name' ? '256px' : 
                                   column.key === 'hospital_code' ? '128px' :
                                   column.key === 'network_status' ? '128px' :
                                   column.key === 'hospital_type' ? '160px' :
                                   column.key === 'disease_name' ? '320px' :
                                   column.key === 'icd_code' ? '96px' :
                                   column.key === 'category' ? '128px' :
                                   column.key === 'severity_level' ? '128px' :
                                   column.key === 'coverage_percentage' ? '128px' :
                                   column.key === 'status' ? '96px' :
                                   'auto'
                        }}
                      >
                        {column.type === 'checkbox' 
                          ? (record[column.key] ? 'Yes' : 'No')
                          : column.key === 'password' || column.key === 'password_hash'
                            ? '••••••••'
                          : column.key === 'permissions' 
                            ? <CollapsiblePermissions permissions={record[column.key]} maxVisible={3} />
                          : (() => {
                              // Check if the field contains date/time information
                              const value = record[column.key]
                              if (!value) return '-'
                              
                              // Check if it's a date string (ISO format, Supabase format, or date only)
                              const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
                              const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/
                              const supabaseDateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\+\d{2}$/
                              
                              if (typeof value === 'string' && (dateRegex.test(value) || dateOnlyRegex.test(value) || supabaseDateRegex.test(value))) {
                                try {
                                  let date
                                  if (supabaseDateRegex.test(value)) {
                                    // Handle Supabase format: 2024-01-15 00:00:00+00
                                    const datePart = value.split(' ')[0]
                                    const timePart = value.split(' ')[1].split('+')[0]
                                    date = new Date(`${datePart}T${timePart}`)
                                  } else {
                                    date = new Date(value)
                                  }
                                  
                                  if (!isNaN(date.getTime())) {
                                    // Format as yyyy-mm-dd hh:mm:ss
                                    const year = date.getFullYear()
                                    const month = String(date.getMonth() + 1).padStart(2, '0')
                                    const day = String(date.getDate()).padStart(2, '0')
                                    const hours = String(date.getHours()).padStart(2, '0')
                                    const minutes = String(date.getMinutes()).padStart(2, '0')
                                    const seconds = String(date.getSeconds()).padStart(2, '0')
                                    
                                    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
                                  }
                                } catch (e) {
                                  // If date parsing fails, return original value
                                }
                              }
                              
                              return value.toString()
                            })()
                        }
                      </div>
                    </td>
                  ))}
                  <td className="px-0.5 py-4 whitespace-nowrap font-medium w-24 sticky right-0 bg-white z-10" style={{fontSize: '9px'}}>
                    <div className="flex space-x-2">
                      {hasPermission('view') && (
                        <button
                          onClick={() => openViewModal(record)}
                          className="text-green-600 hover:text-green-900"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      {hasPermission('edit') && (
                        <button
                          onClick={() => openEditModal(record)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {hasPermission('delete') && (
                        <button
                          onClick={() => handleDelete(record)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-gray-700" style={{fontSize: '12px'}}>
              Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 py-2" style={{fontSize: '12px'}}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold" style={{fontSize: '12px'}}>Add New {title}</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(addFields.length > 0 ? allColumns.filter(col => addFields.includes(col.key)) : columns).map((column) => (
                  <div key={column.key}>
                    <label className="block font-medium text-gray-700 mb-1" style={{fontSize: '10px'}}>
                      {column.label}
                    </label>
                    {renderFormField(column)}
                  </div>
                ))}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                  style={{fontSize: '10px', backgroundColor: '#dc2626', color: 'white', borderColor: '#dc2626'}}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" style={{fontSize: '10px'}}>
                  Add {title}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold" style={{fontSize: '12px'}}>Edit {title}</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(editFields.length > 0 ? allColumns.filter(col => editFields.includes(col.key)) : columns).map((column) => (
                  <div key={column.key}>
                    <label className="block font-medium text-gray-700 mb-1" style={{fontSize: '10px'}}>
                      {column.label}
                    </label>
                    {renderFormField(column)}
                  </div>
                ))}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                  style={{fontSize: '10px', backgroundColor: '#dc2626', color: 'white', borderColor: '#dc2626'}}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" style={{fontSize: '10px'}}>
                  Update {title}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Column Selector Modal */}
      {showColumnSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowColumnSelector(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h3 className="font-semibold" style={{fontSize: '12px'}}>Select Columns</h3>
              </div>
            </div>
            
            {/* Search input for columns */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search columns..."
                value={columnSearchTerm}
                onChange={(e) => setColumnSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{fontSize: '10px'}}
              />
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {allColumns
                .filter(column => 
                  column.label.toLowerCase().includes(columnSearchTerm.toLowerCase()) ||
                  column.key.toLowerCase().includes(columnSearchTerm.toLowerCase())
                )
                .map((column) => {
                const isSelected = selectedColumns.some(col => col.key === column.key)
                return (
                  <label key={column.key} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleColumnToggle(column.key)}
                      className="rounded"
                    />
                    <span className="text-xs">{column.label}</span>
                  </label>
                )
              })}
            </div>
            
            <div className="flex justify-between pt-4 mt-4 border-t">
              <div className="flex space-x-4">
                <button
                  onClick={resetToDefaults}
                  className="text-xs text-gray-600 hover:text-gray-800 underline"
                >
                  Reset to Defaults
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem(`${tableName}_selected_columns`)
                    window.location.reload()
                  }}
                  className="text-red-600 hover:text-red-800 underline"
                  style={{fontSize: '10px'}}
                >
                  Clear All Preferences
                </button>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowColumnSelector(false)}
                  className="btn-secondary"
                  style={{fontSize: '10px', backgroundColor: '#dc2626', color: 'white', borderColor: '#dc2626'}}
                >
                  Cancel
                </button>
                <button
                  onClick={applyColumnSelection}
                  className="btn-primary"
                  style={{fontSize: '10px'}}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">View {title.slice(0, -1)} Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allColumns.map((column) => (
                <div key={column.key} className="space-y-1">
                  <label className="font-medium text-gray-500" style={{fontSize: '10px'}}>
                    {column.label}
                  </label>
                  <div className="text-gray-900 p-2 bg-gray-50 rounded" style={{fontSize: '10px'}}>
                    {column.type === 'checkbox' 
                      ? (viewRecord[column.key] ? 'Yes' : 'No')
                      : column.key === 'password' || column.key === 'password_hash'
                        ? '••••••••'
                      : column.key === 'permissions' 
                        ? (viewRecord[column.key] && typeof viewRecord[column.key] === 'object' 
                            ? Object.keys(viewRecord[column.key]).filter(key => viewRecord[column.key][key] === true).join(', ') || 'No permissions'
                            : viewRecord[column.key]?.toString() || '-')
                      : (() => {
                          // Check if the field contains date/time information
                          const value = viewRecord[column.key]
                          if (!value) return '-'
                          
                          // Check if it's a date string (ISO format, Supabase format, or date only)
                          const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
                          const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/
                          const supabaseDateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\+\d{2}$/
                          
                          if (typeof value === 'string' && (dateRegex.test(value) || dateOnlyRegex.test(value) || supabaseDateRegex.test(value))) {
                            try {
                              let date
                              if (supabaseDateRegex.test(value)) {
                                // Handle Supabase format: 2024-01-15 00:00:00+00
                                const datePart = value.split(' ')[0]
                                const timePart = value.split(' ')[1].split('+')[0]
                                date = new Date(`${datePart}T${timePart}`)
                              } else {
                                date = new Date(value)
                              }
                              
                              if (!isNaN(date.getTime())) {
                                // Format as yyyy-mm-dd hh:mm:ss
                                const year = date.getFullYear()
                                const month = String(date.getMonth() + 1).padStart(2, '0')
                                const day = String(date.getDate()).padStart(2, '0')
                                const hours = String(date.getHours()).padStart(2, '0')
                                const minutes = String(date.getMinutes()).padStart(2, '0')
                                const seconds = String(date.getSeconds()).padStart(2, '0')
                                
                                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
                              }
                            } catch (e) {
                              // If date parsing fails, return original value
                            }
                          }
                          
                          return value.toString()
                        })()
                    }
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end pt-4 mt-4 border-t">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn-secondary"
                style={{fontSize: '10px'}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GenericTable
